/* ============================================================
   useTheme — theme registry, syntax theme, and font scale
   ============================================================ */

import { ref, readonly } from 'vue'
import { HLJS_THEME_CSS } from './hljsThemes'

export type ThemeMode = 'light' | 'dark'

export interface Theme {
  id: string
  name: string
  mode: ThemeMode
  /** Bundled highlight.js stylesheet name. */
  hljs: string
  /** swatch background / foreground for the picker. */
  bg: string
  fg: string
}

/** The brand themes (Redline design handoff) plus a Hotdog novelty. Paper =
 *  default light, Ink = default dark. Ink is the neutral charcoal; Slate is the
 *  cool navy (the two were swapped). Listed in display order. Swatch fg shows the
 *  caret-red so each preview carries the brand mark; bg is the theme surface. */
export const THEMES: readonly Theme[] = [
  { id: 'paper',    name: 'Paper',    mode: 'light', hljs: 'github',      bg: 'oklch(0.991 0.005 85)',  fg: 'oklch(0.660 0.205 27)' },
  { id: 'linen',    name: 'Linen',    mode: 'light', hljs: 'github',      bg: 'oklch(0.974 0.013 88)',  fg: 'oklch(0.660 0.205 27)' },
  { id: 'ink',      name: 'Ink',      mode: 'dark',  hljs: 'github-dark', bg: 'oklch(0.245 0.006 250)', fg: 'oklch(0.715 0.200 30)' },
  { id: 'slate',    name: 'Slate',    mode: 'dark',  hljs: 'github-dark', bg: 'oklch(0.215 0.017 262)', fg: 'oklch(0.715 0.200 30)' },
  { id: 'midnight', name: 'Midnight', mode: 'dark',  hljs: 'github-dark', bg: 'oklch(0.205 0.034 264)', fg: 'oklch(0.715 0.200 30)' },
  { id: 'hotdog',   name: 'Hotdog',   mode: 'light', hljs: 'github',      bg: 'oklch(0.945 0.155 102)', fg: 'oklch(0.580 0.245 28)' },
]

const THEME_KEY = 'md-reader-theme'
const FONT_KEY = 'md-reader-font-size'
const FONT_MIN = 12
const FONT_MAX = 32
const FONT_BASE = 20

// Graceful migration: map legacy / removed theme ids onto the 5 brand themes
// so a stored preference never lands on nothing (and dark stays dark).
const THEME_ALIASES: Record<string, string> = {
  light: 'paper',
  dark: 'ink',
  github: 'paper',
  'solarized-light': 'linen',
  'one-dark': 'ink',
  monokai: 'slate',
}
function normalizeThemeId(id: string | null): string {
  if (id && THEMES.some((t) => t.id === id)) return id
  if (id && THEME_ALIASES[id]) return THEME_ALIASES[id]
  return 'paper'
}

const themeId = ref<string>('paper')
const fontSize = ref<number>(FONT_BASE)

/**
 * Inject the vendored highlight.js theme CSS for the given hljs style name
 * into a single managed <style> element. Replaces the prototype's CDN <link>
 * swap (forbidden by the v6 CSP — styles must be 'self'). The CSS is bundled
 * from the installed highlight.js npm package, so this is same-origin.
 */
function loadHljsThemeCss(hljsName: string): void {
  const css = HLJS_THEME_CSS[hljsName] ?? HLJS_THEME_CSS['github']
  let styleEl = document.getElementById('hljs-theme') as HTMLStyleElement | null
  if (!styleEl) {
    styleEl = document.createElement('style')
    styleEl.id = 'hljs-theme'
    document.head.appendChild(styleEl)
  }
  if (styleEl.textContent !== css) styleEl.textContent = css
}

function applyTheme(id: string): void {
  const theme = THEMES.find((t) => t.id === id) ?? THEMES[0]
  themeId.value = theme.id
  const root = document.documentElement
  root.dataset.theme = theme.id
  root.dataset.mode = theme.mode
  loadHljsThemeCss(theme.hljs)
  try {
    localStorage.setItem(THEME_KEY, theme.id)
  } catch {
    /* storage may be unavailable; theme still applies for the session */
  }
}

function applyFont(px: number): void {
  const clamped = Math.max(FONT_MIN, Math.min(FONT_MAX, px))
  fontSize.value = clamped
  const root = document.documentElement
  root.style.setProperty('--prose-font-size', clamped + 'px')
  root.style.setProperty('--ui-scale', String(clamped / FONT_BASE))
  try {
    localStorage.setItem(FONT_KEY, String(clamped))
  } catch {
    /* ignore */
  }
}

let initialized = false
function initFromStorage(): void {
  if (initialized) return
  initialized = true
  let storedTheme: string | null = null
  let storedFont = FONT_BASE
  try {
    storedTheme = localStorage.getItem(THEME_KEY)
    storedFont = parseInt(localStorage.getItem(FONT_KEY) || String(FONT_BASE), 10)
  } catch {
    /* ignore */
  }
  applyTheme(normalizeThemeId(storedTheme))
  applyFont(Number.isFinite(storedFont) ? storedFont : FONT_BASE)
}

export function useTheme() {
  initFromStorage()

  function setTheme(id: string): void {
    applyTheme(normalizeThemeId(id))
  }
  function bumpFont(delta: number): void {
    applyFont(fontSize.value + delta)
  }
  function setFont(px: number): void {
    applyFont(px)
  }
  function resetFont(): void {
    applyFont(FONT_BASE)
  }

  return {
    themes: THEMES,
    themeId: readonly(themeId),
    fontSize: readonly(fontSize),
    setTheme,
    bumpFont,
    setFont,
    resetFont,
  }
}
